/**
 * Ícones do painel, traçados em `currentColor`.
 *
 * São decorativos: o significado sempre está no rótulo ao lado (o sistema de
 * design proíbe comunicar estado só por forma ou cor). Por isso todos saem do
 * fluxo de leitura com `aria-hidden`.
 */
import type { SVGProps } from 'react';

type PropsIcone = {
  readonly tamanho?: number;
} & Omit<SVGProps<SVGSVGElement>, 'width' | 'height' | 'viewBox' | 'aria-hidden'>;

function Icone({ tamanho = 16, children, ...resto }: PropsIcone) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...resto}
    >
      {children}
    </svg>
  );
}

export function IconeMarca(props: PropsIcone) {
  return (
    <Icone strokeWidth={2.2} {...props}>
      <path d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5z" />
      <path d="M4 7.5 12 12l8-4.5M12 12v9" />
    </Icone>
  );
}

export function IconeVisaoGeral(props: PropsIcone) {
  return (
    <Icone {...props}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </Icone>
  );
}

export function IconeColetas(props: PropsIcone) {
  return (
    <Icone {...props}>
      <path d="M5 8h14l-1.2 11.2a1 1 0 0 1-1 .8H7.2a1 1 0 0 1-1-.8L5 8Z" />
      <path d="M9 8V6.5a3 3 0 0 1 6 0V8" />
    </Icone>
  );
}

export function IconeInventarios(props: PropsIcone) {
  return (
    <Icone {...props}>
      <rect x="3.5" y="4" width="17" height="6" rx="1.5" />
      <rect x="3.5" y="14" width="17" height="6" rx="1.5" />
      <path d="M7 7h.01M7 17h.01" />
    </Icone>
  );
}

export function IconeMovimentacoes(props: PropsIcone) {
  return (
    <Icone {...props}>
      <path d="M4 8h12m-3-4 4 4-4 4" />
      <path d="M20 16H8m3-4-4 4 4 4" />
    </Icone>
  );
}

export function IconeDispositivos(props: PropsIcone) {
  return (
    <Icone {...props}>
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M11 17.5h2" />
    </Icone>
  );
}

export function IconeUsuarios(props: PropsIcone) {
  return (
    <Icone {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </Icone>
  );
}

export function IconeRelatorios(props: PropsIcone) {
  return (
    <Icone {...props}>
      <path d="M5 20V11M12 20V4M19 20v-7" />
      <path d="M3 20h18" />
    </Icone>
  );
}

export function IconeAuditoria(props: PropsIcone) {
  return (
    <Icone {...props}>
      <path d="M12 3l8 3.5v5c0 5-3.4 8-8 9.5-4.6-1.5-8-4.5-8-9.5v-5L12 3Z" />
      <path d="m9 12 2 2 4-4" />
    </Icone>
  );
}

export function IconeConfiguracoes(props: PropsIcone) {
  return (
    <Icone {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" />
    </Icone>
  );
}

export function IconeBusca(props: PropsIcone) {
  return (
    <Icone {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </Icone>
  );
}

export function IconeSeta(props: PropsIcone) {
  return (
    <Icone strokeWidth={2.2} {...props}>
      <path d="m6 9 6 6 6-6" />
    </Icone>
  );
}

export function IconeAvancar(props: PropsIcone) {
  return (
    <Icone strokeWidth={2.2} {...props}>
      <path d="m9 6 6 6-6 6" />
    </Icone>
  );
}

export function IconeVoltar(props: PropsIcone) {
  return (
    <Icone strokeWidth={2.2} {...props}>
      <path d="m15 18-6-6 6-6" />
    </Icone>
  );
}

export function IconeFechar(props: PropsIcone) {
  return (
    <Icone strokeWidth={2} {...props}>
      <path d="M18 6 6 18M6 6l12 12" />
    </Icone>
  );
}

export function IconeConfere(props: PropsIcone) {
  return (
    <Icone strokeWidth={2.2} {...props}>
      <path d="M20 6 9 17l-5-5" />
    </Icone>
  );
}

export function IconeAtencao(props: PropsIcone) {
  return (
    <Icone strokeWidth={2} {...props}>
      <path d="M10.3 4.2 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </Icone>
  );
}

export function IconeSubindo(props: PropsIcone) {
  return (
    <Icone strokeWidth={2.4} {...props}>
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </Icone>
  );
}

export function IconeDescendo(props: PropsIcone) {
  return (
    <Icone strokeWidth={2.4} {...props}>
      <path d="M12 5v14" />
      <path d="m5 12 7 7 7-7" />
    </Icone>
  );
}

export function IconeSair(props: PropsIcone) {
  return (
    <Icone strokeWidth={2} {...props}>
      <path d="M15 17l5-5-5-5M20 12H9M12 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6" />
    </Icone>
  );
}

/* ---------- Moldura e ações ---------- */

/** Painel lateral com uma seta — o botão que recolhe e expande o trilho. */
export function IconeMenuLateral(props: PropsIcone) {
  return (
    <Icone {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M9 4v16" />
      <path d="m15.5 10-2 2 2 2" />
    </Icone>
  );
}

export function IconeAtualizar(props: PropsIcone) {
  return (
    <Icone strokeWidth={2} {...props}>
      <path d="M20 11a8 8 0 0 0-14.5-4.5L4 8" />
      <path d="M4 4v4h4" />
      <path d="M4 13a8 8 0 0 0 14.5 4.5L20 16" />
      <path d="M20 20v-4h-4" />
    </Icone>
  );
}

export function IconeMais(props: PropsIcone) {
  return (
    <Icone strokeWidth={2.2} {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icone>
  );
}

export function IconeExportar(props: PropsIcone) {
  return (
    <Icone strokeWidth={2} {...props}>
      <path d="M12 4v11m-4-4 4 4 4-4" />
      <path d="M4 17v1.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V17" />
    </Icone>
  );
}

export function IconeCalendario(props: PropsIcone) {
  return (
    <Icone {...props}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </Icone>
  );
}

export function IconeArmazem(props: PropsIcone) {
  return (
    <Icone {...props}>
      <path d="M3 10.5 12 4l9 6.5V20H3z" />
      <path d="M8 20v-6h8v6M8 17h8" />
    </Icone>
  );
}

export function IconeEtiqueta(props: PropsIcone) {
  return (
    <Icone {...props}>
      <rect x="4" y="3.5" width="16" height="17" rx="2" />
      <path d="M8 7.5h3v3H8zM13 7.5h3v3h-3zM8 12.5h3v3H8zM13.5 13h2M13.5 15.5h2M8 18h8" />
    </Icone>
  );
}

/* ---------- Assuntos dos indicadores ---------- */

export function IconeOperacoes(props: PropsIcone) {
  return (
    <Icone {...props}>
      <path d="M4 17 9.5 11l3.5 3.5L20 7" />
      <path d="M15 7h5v5" />
    </Icone>
  );
}

export function IconeFila(props: PropsIcone) {
  return (
    <Icone {...props}>
      <rect x="3.5" y="5" width="17" height="4" rx="1.5" />
      <rect x="3.5" y="15" width="17" height="4" rx="1.5" />
      <path d="M12 9v6M9.5 12.5 12 15l2.5-2.5" />
    </Icone>
  );
}
